import { F_MapMarkerVariantIconFragment } from '__generated__v2/graphql';
import { isDefined } from 'libs/utils/typesafe/isDefined';
import { reverse } from 'lodash';
import { useCtxMapView } from 'pages/special/OsloDesignFair/map/CtxMapView';
import { VariantAvailableIcons } from 'pages/special/OsloDesignFair/map/mapConstants';
import { ClusterMarker } from 'pages/special/OsloDesignFair/map/markers/view/VariantIconMarkers/ClusterMarker';
import { useComputeMarkerSizes } from 'pages/special/OsloDesignFair/map/markers/view/VariantIconMarkers/useComputeMarkerSizes';
import { VariantIconMarker } from 'pages/special/OsloDesignFair/map/markers/view/VariantIconMarkers/VariantIconMarker';
import { VariantSizes } from 'pages/special/OsloDesignFair/map/markers/view/VariantIconMarkers/VariantIconMarker/shared/variantUtils';
import { useEffect, useMemo } from 'react';
import Supercluster from 'supercluster';

interface Props {
    markers: F_MapMarkerVariantIconFragment[];
}

const useDivideMarkersAndClusters = (
    markers: F_MapMarkerVariantIconFragment[],
    isEditMode: boolean
) => {
    const superclusterObj = new Supercluster({
        log: false,
        radius: 60,
        extent: 256,
        minZoom: 24,
        maxZoom: 24
    }).load(
        markers.map((e) => ({
            type: 'Feature',
            properties: {
                markerId: e.id
            },
            geometry: {
                type: 'Point',
                coordinates: [e.lng, e.lat]
            }
        }))
    );

    if (isEditMode) {
        return {
            markers,
            clusters: [],
            getMarkersInCluster: (clusterId: number) => {
                return [clusterId.toString()];
            }
        };
    }

    const superclusterResult = superclusterObj.getClusters([-180, -90, 180, 90], 24);
    const markerIdsWithotClusters = new Set<string>(
        superclusterResult.map((e) => e.properties.markerId).filter(isDefined)
    );

    const pointsWithoutClusters = markers.filter((m) => markerIdsWithotClusters.has(m.id));
    const clusters = superclusterResult
        .filter((e) => e.properties.cluster)
        .map((e) => ({
            clusterId: e.properties.cluster_id,
            pointCount: e.properties.point_count,
            pointCountLabel: e.properties.point_count_abbreviated,
            coordinates: {
                lng: e.geometry.coordinates[0],
                lat: e.geometry.coordinates[1]
            }
        }));
    return {
        markers: pointsWithoutClusters,
        clusters,
        getMarkersInCluster: (clusterId: number) => {
            return superclusterObj
                .getLeaves(clusterId)
                .map((e) => e.properties.markerId)
                .filter(isDefined);
        }
    };
};

export const VariantIconMarkers = (props: Props) => {
    const ctxMapView = useCtxMapView();
    const globalConfig = ctxMapView.markerSettings['variant_icon'];

    const markersBeforeCluster = useMemo(
        () => props.markers.filter((e) => !e.isHidden),
        [props.markers]
    );

    const { markers, clusters, getMarkersInCluster } = useDivideMarkersAndClusters(
        markersBeforeCluster,
        ctxMapView.isEditMode
    );

    const markerSizes = useComputeMarkerSizes({
        markers: markers,
        highlightedMarkerIds: ctxMapView.highlightedMarkerIds,
        mapRef: ctxMapView.mapRef,
        collisionPaddingLarge: globalConfig.collisionPaddingLarge,
        collisionPaddingMiddle: globalConfig.collisionPaddingMiddle
    });

    useEffect(() => {
        markerSizes.triggerUpdateImmediately();
    }, []);

    useEffect(() => {
        function triggerUpdate() {
            markerSizes.triggerUpdate();
        }

        ctxMapView.mapRef?.current?.on('zoom', triggerUpdate);
        ctxMapView.mapRef?.current?.on('moveend', triggerUpdate);
        return () => {
            ctxMapView.mapRef?.current?.off('zoom', triggerUpdate);
            ctxMapView.mapRef?.current?.off('moveend', triggerUpdate);
        };
    }, [ctxMapView.mapRef?.current, markers]);

    useEffect(() => {
        markerSizes.triggerUpdateImmediately();
    }, [ctxMapView.highlightedMarkerIds?.join(';'), markers.length]);

    return (
        <>
            {clusters.map((cluster) => {
                getMarkersInCluster(cluster.clusterId);
                return (
                    <ClusterMarker
                        key={cluster.clusterId}
                        pointCount={cluster.pointCount}
                        isActive={false}
                        isWide={ctxMapView.isWide}
                        fonts={ctxMapView.fonts}
                        mapRef={ctxMapView.mapRef}
                        clusterId={cluster.clusterId}
                        lng={cluster.coordinates.lng}
                        lat={cluster.coordinates.lat}
                        forceHighlightSelectableMarkers={ctxMapView.forceHighlightSelectableMarkers}
                        onClick={() => {
                            const markerIds = getMarkersInCluster(cluster.clusterId);
                            ctxMapView.onMarkersClicked(markerIds);
                        }}
                        selectable
                    />
                );
            })}
            {reverse(markers).map((marker) => {
                const isActive = ctxMapView.highlightedMarkerIds?.includes(marker.id) ?? false;

                return (
                    <VariantIconMarker
                        markerType={marker.markerType}
                        iconName={marker.iconName as VariantAvailableIcons}
                        customImageUrl={marker.imageUrl}
                        imageShowBg={marker.imageShowBg}
                        imageShowContain={marker.imageShowContain}
                        eventShowImage={marker.eventShowImage}
                        isActive={isActive}
                        forceHighlightSelectableMarkers={ctxMapView.forceHighlightSelectableMarkers}
                        imageUrl={marker.imageUrl}
                        isWide={ctxMapView.isWide}
                        mapRef={ctxMapView.mapRef}
                        eventDate={marker.eventDate}
                        language={ctxMapView.language}
                        isEditMode={ctxMapView.isEditMode}
                        key={marker.id}
                        lat={marker.lat}
                        lng={marker.lng}
                        highlighted={marker.highlighted ?? false}
                        sublabel={marker.sublabel}
                        selectable={marker.selectable ?? false}
                        size={markerSizes.sizes.get(marker.id) as VariantSizes}
                        markerId={marker.id}
                        markerGlobalSettings={globalConfig}
                        text={marker.text}
                        color={marker.color}
                        onClick={(id: string) => ctxMapView.onMarkersClicked([id])}
                    />
                );
            })}
        </>
    );
};
